export type SourceDocKind = 'pdf' | 'image' | 'text' | 'download'

const IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff'
])

const TEXT_TYPES = new Set([
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/xml',
  'application/json',
  'application/xml',
  'application/csv'
])

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif'])
const TEXT_EXTS = new Set(['txt', 'csv', 'log', 'md', 'xml', 'json'])
/**
 * Spreadsheet workbooks the source route may remap to a generated PDF.
 * Mirrors `isSpreadsheetWorkbookPath` / `EXCEL_EXTENSIONS` in
 * `@atbash-labs/ontology-ingest` — keep in sync if that list grows.
 */
const WORKBOOK_EXTS = new Set(['xls', 'xlsx', 'xlsm', 'xlsb', 'ods'])
/**
 * Office documents the source route may remap to a generated PDF.
 * Mirrors `OFFICE_DOCUMENT_EXTENSIONS` / `isOfficeDocumentPath` in
 * `@atbash-labs/ontology-ingest` — keep in sync if that list grows.
 */
const OFFICE_EXTS = new Set(['doc', 'docx', 'ppt', 'pptx', 'vsd', 'vsdx', 'rtf'])

/** Soft cap for inline text preview (~2 MB). */
export const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024

function extensionOf(path: string): string {
  const base = path.split(/[/\\]/).pop() ?? path
  const dot = base.lastIndexOf('.')
  if (dot < 0) return ''
  return base.slice(dot + 1).toLowerCase()
}

/**
 * Derive how to render a source document from the response Content-Type
 * (server truth), falling back to the path extension.
 */
export function sourceDocKind(contentType: string, path: string): SourceDocKind {
  const type = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
  if (type === 'application/pdf') return 'pdf'
  if (IMAGE_TYPES.has(type)) return 'image'
  if (TEXT_TYPES.has(type) || type.startsWith('text/')) return 'text'
  if (type && type !== 'application/octet-stream') return 'download'

  const ext = extensionOf(path)
  if (ext === 'pdf') return 'pdf'
  if (IMAGE_EXTS.has(ext)) return 'image'
  if (TEXT_EXTS.has(ext)) return 'text'
  return 'download'
}

/** True for spreadsheet workbooks the source route may remap to PDF. */
export function isSpreadsheetWorkbookPath(path: string): boolean {
  return WORKBOOK_EXTS.has(extensionOf(path))
}

/**
 * True when the source route may remap this path to a generated PDF under
 * `.transformed/`. Mirrors `isPdfRemappableSourcePath` in ontology-ingest.
 */
export function isPdfRemappableSourcePath(path: string): boolean {
  const ext = extensionOf(path)
  return WORKBOOK_EXTS.has(ext) || OFFICE_EXTS.has(ext)
}

/**
 * True when opening the viewer should fetch corpus bytes for an in-app
 * preview attempt. Non-previewable paths skip the fetch until Download;
 * workbooks and office docs always fetch so the server can serve a remapped PDF.
 */
export function shouldFetchSourceDocForPreview(path: string): boolean {
  if (isPdfRemappableSourcePath(path)) return true
  return sourceDocKind('', path) !== 'download'
}

export function sourceFileBasename(path: string): string {
  const trimmed = path.replace(/\\/g, '/').replace(/\/+$/, '')
  const parts = trimmed.split('/')
  return parts[parts.length - 1] || path
}

export interface PreparedTextPreview {
  text: string
  truncated: boolean
}

/** Pretty-print JSON when possible; truncate oversized text for inline preview. */
export function prepareTextPreview(
  raw: string,
  maxBytes = TEXT_PREVIEW_MAX_BYTES
): PreparedTextPreview {
  let text = raw
  const trimmed = raw.trim()
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      text = JSON.stringify(JSON.parse(raw), null, 2)
    } catch {
      // keep raw
    }
  }

  const encoder = new TextEncoder()
  if (encoder.encode(text).byteLength <= maxBytes) {
    return { text, truncated: false }
  }

  // Truncate by UTF-16 code units approximating byte budget; good enough for the notice.
  let end = Math.min(text.length, maxBytes)
  while (end > 0 && encoder.encode(text.slice(0, end)).byteLength > maxBytes) {
    end = Math.floor(end * 0.9)
  }
  return { text: text.slice(0, end), truncated: true }
}
